import React, { useState, useMemo } from 'react';
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMinutes,
    isAfter,
    isBefore,
    isSameMinute
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './InterviewPortalCalendar.css';

const InterviewPortalCalendar = ({
    availableTimeslots = [],
    onStartTimeSelect,
    minDate = new Date(),
    maxDate,
    theme = 'light',
    eventStartTimeSpreadInMinutes = 30
}) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);

    // Month navigation
    const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
    const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

    // Calendar grid generation
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = eachDayOfInterval({ start: startDate, end: endDate });

    // Calculate dates that have available slots
    const availableDates = useMemo(() => {
        const dates = [];
        availableTimeslots.forEach(ts => {
            const start = ts.startTime ? new Date(ts.startTime) : null;
            const end = ts.endTime ? new Date(ts.endTime) : null;
            if (!start || !end) return;

            const intervalDays = eachDayOfInterval({ start, end });
            intervalDays.forEach(day => {
                if (isSameMonth(day, monthStart)) {
                    // Check if within bounds
                    const dayStart = new Date(day);
                    dayStart.setHours(0, 0, 0, 0);
                    const dayEnd = new Date(day);
                    dayEnd.setHours(23, 59, 59, 999);

                    if (isAfter(dayEnd, minDate) && (!maxDate || isBefore(dayStart, maxDate))) {
                        dates.push(format(day, 'yyyy-MM-dd'));
                    }
                }
            });
        });
        return [...new Set(dates)];
    }, [availableTimeslots, monthStart, minDate, maxDate]);

    // Generate slots for selected date
    const availableSlotsForDate = useMemo(() => {
        if (!selectedDate) return [];

        const slots = [];
        availableTimeslots.forEach(ts => {
            let current = new Date(ts.startTime);
            const end = new Date(ts.endTime);

            while (isBefore(current, end) || isSameMinute(current, end)) {
                if (isSameDay(current, selectedDate)) {
                    // Check if it's within min/max bounds
                    if (isAfter(current, minDate) && (!maxDate || isBefore(current, maxDate))) {
                        slots.push(new Date(current));
                    }
                }
                current = addMinutes(current, eventStartTimeSpreadInMinutes);
            }
        });

        return slots.sort((a, b) => a - b);
    }, [selectedDate, availableTimeslots, minDate, maxDate, eventStartTimeSpreadInMinutes]);

    const handleDayClick = (day) => {
        if (isSameMonth(day, monthStart)) {
            setSelectedDate(day);
            setSelectedSlot(null);
        }
    };

    const handleSlotClick = (slot) => {
        setSelectedSlot(slot);
    };

    const handleConfirm = () => {
        if (selectedSlot && onStartTimeSelect) {
            onStartTimeSelect({ startTime: selectedSlot });
        }
    };

    return (
        <div className={`interview-portal-calendar ${theme}-theme`}>
            <div className="calendar-container">
                {/* Left Side: Calendar */}
                <div className="calendar-left">
                    <div className="calendar-header">
                        <button onClick={prevMonth} className="nav-btn"><ChevronLeft size={20} /></button>
                        <h3 className="month-title">{format(currentMonth, 'MMMM yyyy')}</h3>
                        <button onClick={nextMonth} className="nav-btn"><ChevronRight size={20} /></button>
                    </div>

                    <div className="days-of-week">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                            <div key={day} className="dow-cell">{day}</div>
                        ))}
                    </div>

                    <div className="date-grid">
                        {days.map((day, idx) => {
                            const isSelected = selectedDate && isSameDay(day, selectedDate);
                            const isCurrentMonth = isSameMonth(day, monthStart);
                            const isToday = isSameDay(day, new Date());
                            const isAvailable = availableDates.includes(format(day, 'yyyy-MM-dd'));

                            return (
                                <div
                                    key={idx}
                                    className={`date-cell ${!isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isAvailable ? 'available' : ''}`}
                                    onClick={() => handleDayClick(day)}
                                >
                                    <span className="date-number">{format(day, 'd')}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Divider */}
                <div className="vertical-divider"></div>

                {/* Right Side: Timeslots */}
                <div className="calendar-right">
                    <h3 className="selected-date-title">
                        {selectedDate ? format(selectedDate, 'EEEE, MMMM do') : 'Select a date'}
                    </h3>

                    <div className="slots-container">
                        {availableSlotsForDate.length > 0 ? (
                            <div className="slots-grid">
                                {availableSlotsForDate.map((slot, idx) => (
                                    <button
                                        key={idx}
                                        className={`${theme === 'dark' ? 'button' : 'border-btn !text-textPrimary'} ${selectedSlot && isSameMinute(slot, selectedSlot) ? 'selected-slot' : ''} mb-2 w-30`}
                                        onClick={() => handleSlotClick(slot)}
                                    >
                                        <span>{format(slot, 'h:mm a')}</span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="no-slots-text">
                                Please select a date within the range {format(minDate, 'MMM do')} to {maxDate ? format(maxDate, 'MMM do') : ''}
                            </p>
                        )}
                    </div>

                    <div className="confirm-container">
                        <button
                            className="confirm-btn"
                            disabled={!selectedSlot}
                            onClick={handleConfirm}
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InterviewPortalCalendar;
